// @ts-nocheck
// TODO: align with current types (Phase 4a) — bucket 2/3 + DS strictness regressions
/**
 * Assign Role Modal
 *
 * Modal dialog for assigning a role to a user.
 * Supports company context and expiry date.
 */

import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@lognai/synapse-design-system-v2';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { Input } from '@lognai/synapse-design-system-v2';
import { Checkbox } from '@lognai/synapse-design-system-v2';
import { useToast } from '@lognai/synapse-design-system-v2';
import { FormModal } from '../shared';
import { usePlatformRoles, useCompanies, useAssignUserRole } from '../../hooks';

/** Sentinel for "no company context" — Radix Select disallows empty-string item values. */
const PLATFORM_WIDE = '__platform_wide__';

const assignRoleSchema = z.object({
  roleId: z.string().min(1, 'Role is required'),
  companyId: z.string().optional(),
  expiresAt: z.string().optional(),
  hasExpiry: z.boolean().default(false),
});

type AssignRoleFormData = z.infer<typeof assignRoleSchema>;

export interface AssignRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
  onSuccess?: () => void;
}

export function AssignRoleModal({
  open,
  onOpenChange,
  userId,
  userName,
  onSuccess,
}: AssignRoleModalProps) {
  const { toast } = useToast();
  const { data: roles = [], isLoading: rolesLoading } = usePlatformRoles();
  const { data: companiesData, isLoading: companiesLoading } = useCompanies();
  const companies = companiesData?.items ?? [];
  const assignRole = useAssignUserRole();

  const form = useForm<AssignRoleFormData>({
    resolver: zodResolver(assignRoleSchema),
    defaultValues: {
      roleId: '',
      companyId: '',
      expiresAt: '',
      hasExpiry: false,
    },
  });

  const hasExpiry = form.watch('hasExpiry');

  const handleSubmit = async (data: AssignRoleFormData) => {
    try {
      const assignment = {
        roleId: data.roleId,
        companyId: data.companyId || undefined,
        expiresAt: data.hasExpiry && data.expiresAt ? new Date(data.expiresAt) : undefined,
      };

      await assignRole.mutateAsync({ userId, assignment });

      toast({
        title: 'Role assigned',
        description: `Successfully assigned role to ${userName || 'user'}`,
      });

      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to assign role',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Assign Role"
      description={`Assign a role to ${userName || 'this user'}`}
      size="md"
      submitLabel="Assign Role"
      isSubmitting={assignRole.isPending}
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Role Selection */}
          <FormField
            control={form.control}
            name="roleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role *</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={rolesLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        <div>
                          <div className="font-medium">{role.name}</div>
                          {role.description && (
                            <div className="text-xs text-muted-foreground">{role.description}</div>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
                <FormDescription>
                  Select the role to assign to this user
                </FormDescription>
              </FormItem>
            )}
          />

          {/* Company Context (Optional) */}
          <FormField
            control={form.control}
            name="companyId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Context (Optional)</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(value === PLATFORM_WIDE ? '' : value)}
                  value={field.value || PLATFORM_WIDE}
                  disabled={companiesLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Platform-wide (all companies)" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={PLATFORM_WIDE}>Platform-wide (all companies)</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
                <FormDescription>
                  Leave empty for platform-wide role, or select a specific company
                </FormDescription>
              </FormItem>
            )}
          />

          {/* Expiry Date Toggle */}
          <FormField
            control={form.control}
            name="hasExpiry"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Set expiry date</FormLabel>
                  <FormDescription>
                    Role will automatically expire after the specified date
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {/* Expiry Date Input */}
          {hasExpiry && (
            <FormField
              control={form.control}
              name="expiresAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expiry Date *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    Role assignment will expire at the end of this date
                  </FormDescription>
                </FormItem>
              )}
            />
          )}
        </form>
      </Form>
    </FormModal>
  );
}